/**
 * Saved-itineraries sub-FAB.
 *
 * Action-mode sub-FAB that opens the itinerary panel in saved-itinerary list mode.
 * Shows a badge with the count of unfinished saved itineraries when greater than zero.
 * Closes the parent burst on press so the expanded ring does not stay in the way
 * of the itinerary panel.
 */

import React, { useCallback, useMemo } from "react";
import savedItineraryIcon from "../../../../image/icons/floating/SavedItineraryIcon.svg";
import { getUIColorPalette } from "../../../../theme/ColorPalette";
import { BURST_SUB_FAB_GREEN_FILTER } from "../../floatingAction.configuration";
import { useFloatingActionBurstContext } from "../FloatingActionBurstContext";
import SubFloatingAction from "./SubFloatingAction";

/** Props exposed by the saved-itineraries sub-FAB. */
export interface SavedItinerariesSubFloatingActionProps {

    /** Number of saved itineraries still within the post-arrival grace window. */
    "futureSavedCount": number;

    /** Fired when the user opens the saved-itinerary list. */
    "onOpenSavedItineraries": () => void;

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;
}

/**
 * Renders the saved-itineraries sub-FAB with an optional active-count badge.
 *
 * @param props Component props. See {@link SavedItinerariesSubFloatingActionProps}.
 * @returns Configured action sub-FAB.
 */
const SavedItinerariesSubFloatingAction: React.FC<SavedItinerariesSubFloatingActionProps> = ( {
    futureSavedCount,
    onOpenSavedItineraries,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const requestBurstClose = useFloatingActionBurstContext()?.requestBurstClose;
    const showBadge = futureSavedCount > 0;
    const badgeLabel = futureSavedCount > 99 ? "99+" : String( futureSavedCount );

    /**
     * Collapse the burst then open the saved-itinerary list so the panel
     * is not obscured by the expanded sub-FAB ring.
     */
    const handleOpenSavedItineraries = useCallback( (): void => {
        requestBurstClose?.();
        onOpenSavedItineraries();
    }, [ onOpenSavedItineraries, requestBurstClose ] );

    return (
        <div className="relative flex">
            <SubFloatingAction
                ariaLabel="Itinéraires sauvegardés"
                iconAlt="Itinéraires sauvegardés"
                iconFilter={BURST_SUB_FAB_GREEN_FILTER}
                iconSrc={savedItineraryIcon}
                isDarkUi={isDarkUi}
                kind="action"
                onAction={handleOpenSavedItineraries}
            />
            {showBadge
                ? (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none"
                        style={{
                            "backgroundColor": palette.brandPrimary,
                            "color":           palette.selectorActiveText,
                        }}
                    >
                        {badgeLabel}
                    </span>
                )
                : null}
        </div>
    );
};

export default SavedItinerariesSubFloatingAction;
